import i18n from '../config/i18n';
import { authenticatedFetch } from './authService';

export type ContentTipo = 'artigo' | 'video' | 'caso';
export type ContentLang = 'pt' | 'es';
export type FeedSort = 'recent' | 'views' | 'week';
export type MeuFeedback = 'gostei' | 'nao_gostei' | null;

export interface TemaRef {
  id: number;
  slug: string | null;
  nome: string;
  cor: string | null;
}

export interface FonteOriginal {
  texto?: string | null;
  doi?: string | null;
  url?: string | null;
  metodo?: string | null;
  confianca?: string | null;
  citacao_curta?: string | null;
}

export interface Blocos {
  pontos_chave?: string[];
}

export interface CasoPergunta {
  enunciado: string;
  alternativas: string[];
  // Índice (base 0) da alternativa correta.
  correta: number;
}

export interface CasoConteudo {
  historia: string;
  video_url: string | null;
  video_duracao_s: number | null;
  pergunta?: CasoPergunta | null;
}

// Onde o aluno parou (null/ausente = nunca abriu).
export interface Progresso {
  percentual: number;
  videoS: number | null;
  concluido: boolean;
  atualizadoEm: string;
}

export interface ProgressUpdate {
  percentual?: number;
  videoS?: number;
  concluido?: boolean;
}

export interface ArticleCard {
  id: string;
  grupoId: string;
  idOrigem: string | null;
  slug: string;
  tipo: ContentTipo;
  language: ContentLang;
  title: string;
  resumoCard: string | null;
  temas: TemaRef[];
  colecoes: string[];
  publishedAt: string | null;
  tempoLeituraMin: number | null;
  casoDuracaoS: number | null;
  capaUrl: string | null;
  imagemFallback: string | null;
  visualizacoes: number;
  fonteOriginal: FonteOriginal | null;
  isSaved: boolean;
  progresso?: Progresso | null;
}

export interface ArticleVideoRef {
  id: string;
  url: string;
}

export interface ArticleFull extends ArticleCard {
  author: string | null;
  referencia: string | null;
  content: string;
  blocos: Blocos | null;
  caso: CasoConteudo | null;
  videos: ArticleVideoRef[];
  isRead: boolean;
  meuFeedback: MeuFeedback;
  traducaoSlug: string | null;
  relacionados: ArticleCard[];
}

export interface ArticleRedirect {
  redirectSlug: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

export interface FeedResponse extends Paginated<ArticleCard> {
  busca: { termo: string; termosExpandidos: string[] } | null;
}

export interface HomeResponse {
  featured: ArticleCard | null;
  recentes: ArticleCard[];
  voceperdeu: ArticleCard[];
  casoDestaque: ArticleCard | null;
  atualizacoes: ArticleCard[];
  // Continue de onde parou.
  continuar?: ArticleCard[];
  total: number;
}

export interface FeedParams {
  page?: number;
  tipo?: ContentTipo;
  tema?: string;
  sort?: FeedSort;
  search?: string;
}

async function readError(response: Response, fallbackKey: string): Promise<Error> {
  try {
    const body = await response.json();
    const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    return new Error(message || i18n.t(fallbackKey));
  } catch {
    return new Error(i18n.t(fallbackKey));
  }
}

/** 404 da API: o item não existe (ou não está mais publicado). */
export class NotFoundError extends Error {}

async function getJson<T>(endpoint: string, fallbackKey: string): Promise<T> {
  const response = await authenticatedFetch(endpoint, { method: 'GET' });
  if (response.status === 404) throw new NotFoundError((await readError(response, fallbackKey)).message);
  if (!response.ok) throw await readError(response, fallbackKey);
  return (await response.json()) as T;
}

async function send(
  endpoint: string,
  method: 'POST' | 'PUT' | 'DELETE',
  fallbackKey: string,
  body?: unknown,
): Promise<Response> {
  const response = await authenticatedFetch(endpoint, {
    method,
    ...(body !== undefined
      ? { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }
      : {}),
  });
  if (!response.ok) throw await readError(response, fallbackKey);
  return response;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
  }
  const out = qs.toString();
  return out ? `?${out}` : '';
}

const slugPath = (slug: string) => `/articles/${encodeURIComponent(slug)}`;

export function getFeed(params: FeedParams = {}): Promise<FeedResponse> {
  return getJson(
    `/articles${query({
      page: params.page,
      tipo: params.tipo,
      tema: params.tema,
      sort: params.sort,
      search: params.search?.trim(),
    })}`,
    'errors.fetchArticles',
  );
}

export function getHome(): Promise<HomeResponse> {
  return getJson('/articles/home', 'errors.fetchArticles');
}

/** `original`: abre este slug mesmo sendo de outro idioma (link "outro idioma"). */
export function getArticleBySlug(
  slug: string,
  original = false,
): Promise<ArticleFull | ArticleRedirect> {
  return getJson(
    `${slugPath(slug)}${query({ original: original ? '1' : undefined })}`,
    'errors.fetchArticle',
  );
}

export function isRedirect(value: ArticleFull | ArticleRedirect | undefined): value is ArticleRedirect {
  return !!value && 'redirectSlug' in value;
}

const VIEWED_KEY = 'nn_viewed:';

/**
 * Conta a abertura do item uma vez por sessão do navegador (sessionStorage:
 * recarregar a aba não conta de novo; outra aba/sessão conta). Fire-and-forget:
 * nunca lança nem bloqueia a página.
 */
export function registerView(articleId: string, slug: string): void {
  try {
    if (sessionStorage.getItem(VIEWED_KEY + articleId)) return;
    sessionStorage.setItem(VIEWED_KEY + articleId, '1');
  } catch {
    // sessionStorage bloqueado: segue sem dedupe local.
  }
  authenticatedFetch(`${slugPath(slug)}/view`, { method: 'POST' }).catch(() => {
    // falha ao gravar evento nunca derruba a página
  });
}

/**
 * Alternativa confirmada na pergunta do caso (índice base 0). O acerto é
 * calculado no servidor. Fire-and-forget: nunca lança nem bloqueia a página.
 */
export function registerCaseAnswer(slug: string, alternativa: number): void {
  authenticatedFetch(`${slugPath(slug)}/caso/resposta`, {
    method: 'POST',
    body: JSON.stringify({ alternativa }),
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {
    // falha ao gravar evento nunca derruba a página
  });
}

export async function markArticleRead(slug: string): Promise<void> {
  await send(`${slugPath(slug)}/read`, 'POST', 'errors.markArticleRead');
}

export async function markArticleUnread(slug: string): Promise<void> {
  await send(`${slugPath(slug)}/read`, 'DELETE', 'errors.markArticleUnread');
}

export async function saveArticle(slug: string): Promise<void> {
  await send(`${slugPath(slug)}/save`, 'POST', 'errors.saveArticle');
}

export async function unsaveArticle(slug: string): Promise<void> {
  await send(`${slugPath(slug)}/save`, 'DELETE', 'errors.saveArticle');
}

export function getSaved(params: { page?: number; tipo?: ContentTipo } = {}): Promise<Paginated<ArticleCard>> {
  return getJson(`/articles/saved${query(params)}`, 'errors.fetchArticles');
}

export function getInProgress(params: { page?: number; tipo?: ContentTipo } = {}): Promise<Paginated<ArticleCard>> {
  return getJson(`/articles/in-progress${query(params)}`, 'errors.fetchArticles');
}

export function getHistory(params: { page?: number; tipo?: ContentTipo } = {}): Promise<Paginated<ArticleCard>> {
  return getJson(`/articles/history${query(params)}`, 'errors.fetchArticles');
}

/**
 * Salva onde o aluno parou. Corpo vazio só marca o acesso (histórico).
 * `keepalive` deixa a requisição terminar mesmo saindo da página.
 * Fire-and-forget: nunca lança.
 */
export function saveProgress(slug: string, update: ProgressUpdate, keepalive = false): void {
  authenticatedFetch(`${slugPath(slug)}/progress`, {
    method: 'PUT',
    body: JSON.stringify(update),
    headers: { 'Content-Type': 'application/json' },
    ...(keepalive ? { keepalive: true } : {}),
  }).catch(() => {
    // falha ao gravar progresso nunca derruba a página
  });
}

export async function getSavedCount(): Promise<number> {
  const body = await getJson<{ total: number }>('/articles/saved/count', 'errors.fetchArticles');
  return body.total;
}

export async function setFeedback(slug: string, gostou: boolean): Promise<MeuFeedback> {
  const response = await send(`${slugPath(slug)}/feedback`, 'PUT', 'errors.feedbackArticle', { gostou });
  return ((await response.json()) as { meuFeedback: MeuFeedback }).meuFeedback;
}

export async function clearFeedback(slug: string): Promise<MeuFeedback> {
  const response = await send(`${slugPath(slug)}/feedback`, 'DELETE', 'errors.feedbackArticle');
  return ((await response.json()) as { meuFeedback: MeuFeedback }).meuFeedback;
}
